import { updateConfig } from "State/Admin/AdminSettings/ConfigurationSlice";
import { useAppDispatch, useAppSelector } from "State/Hooks";
import { RootState } from "State/store";
import Toggle from "../../Notification/Toggle";
import PickupLocationSettings from "./PickupLocationSettings";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

const ShipmentExternalAPISettings = () => {
  const dispatch = useAppDispatch();
  const config = useAppSelector(
    (state: RootState) => state.configuration.config,
  );
  const [showTwilioAuthToken, setShowTwilioAuthToken] = useState(false);
  const [showWhatsappApiToken, setShowWhatsappApiToken] = useState(false);

  const shippingChargeOption = config.isShippingChargesApplicable ? "applicable" : "none";
  const freeShippingThreshold = config.shippingChargeThresholdAmount ?? 0;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    input:
      | "delhiveryApiKey"
      | "whatsappBusinessApiKey"
      | "whatsappApiToken"
      | "whatsappApiUrl"
      | "whatsappPhoneNumberId"
      | "fast2SmsApiKey"
      | "twilioAccountSid"
      | "twilioAuthToken"
      | "twilioOutgoingSmsNumber"
      | "f2sOtpVerificationSenderId"
      | "f2sOrderSuccessSenderId",
  ) => {
    dispatch(updateConfig({ [input]: e.target.value }));
  };

  const handleNumberInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    input: "f2sOtpVerificationMessageId" | "f2sOrderSuccessMessageId",
  ) => {
    const val = e.target.value;
    dispatch(updateConfig({ [input]: val === "" ? null : Number(val) }));
  };

  const handleToggleInput = (
    enabled: boolean,
    input: "isShippingApiEnabled" | "isWhatsappBusinessApiEnabled",
  ) => {
    dispatch(updateConfig({ [input]: enabled }));
  };

  return (
    <div className="space-y-8">
      {/* ================= SHIPPING ================= */}
      <div>
        <h3 className="text-sm font-semibold">Shipping</h3>
        <p className="text-xs text-gray-500 mt-1">Configure delhivery</p>

        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-700">Enable Delhivery</p>
            <Toggle
              enabled={config.isShippingApiEnabled}
              onChange={(enabled: boolean) =>
                handleToggleInput(enabled, "isShippingApiEnabled")
              }
            />
          </div>

          {config.isShippingApiEnabled && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Delhivery API Key
                  </label>
                  <input
                    type="text"
                    value={config.delhiveryApiKey}
                    onChange={(e) => handleInputChange(e, "delhiveryApiKey")}
                    className="w-full px-3 py-2 border rounded-md text-xs"
                    placeholder="Enter Delhivery API Key"
                  />
                </div>
                <div className="flex items-center justify-center bg-gray-300 border rounded-md px-3 py-2">
                  <img
                    src="/logo/delhivery.svg"
                    alt="Delhivery"
                    className="w-20 sm:w-28 object-contain"
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                  Delhivery API URL
                  {/* <span className="text-[10px] font-normal text-gray-400 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">Read only</span> */}
                </label>
                <input
                  type="text"
                  value={config.delhiveryApiUrl || ""}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-xs bg-gray-100 text-gray-400 cursor-not-allowed select-none"
                />
              </div>
            </div>
          )}
          {/* ================= DELIVERY CONFIGURATION ================= */}
{config.isShippingApiEnabled && (
  <div>
    <div className="mt-4">
      <PickupLocationSettings />
    </div>
  </div>
)}

        </div>
      </div>

      {/* ================= SHIPPING CHARGES ================= */}
      {config.isShippingApiEnabled && <div className="pt-6 border-t">
        <h3 className="text-sm font-semibold">Shipping Charges</h3>
        <p className="text-xs text-gray-500 mt-1">
          Configure when shipping charges are applied to orders
        </p>

        <div className="mt-4 space-y-3">
          {/* Option 1 — No Shipping Charges */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="shippingChargeOption"
              value="none"
              checked={shippingChargeOption === "none"}
              onChange={() => dispatch(updateConfig({ isShippingChargesApplicable: false }))}
              className="accent-blue-600"
            />
            <span className="text-xs text-gray-700">No Shipping Charges</span>
          </label>

          {/* Option 2 — Shipping Charges Applicable */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="shippingChargeOption"
              value="applicable"
              checked={shippingChargeOption === "applicable"}
              onChange={() => dispatch(updateConfig({ isShippingChargesApplicable: true }))}
              className="accent-blue-600"
            />
            <span className="text-xs text-gray-700">
              Shipping Charges Applicable
            </span>
          </label>

          {/* Threshold field — visible only when option 2 is selected */}
          {shippingChargeOption === "applicable" && (
            <div className="ml-5 mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Add amount below which shipping charges will be applied
              </label>
              <p className="text-[11px] text-gray-400 mb-2">
                Orders below this amount will incur shipping charges.
              </p>
              <div className="relative w-full sm:w-64">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                  ₹
                </span>
                <input
                  type="number"
                  min={0}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={freeShippingThreshold || ""}
                  onChange={(e) => dispatch(updateConfig({ shippingChargeThresholdAmount: e.target.value === "" ? 0 : Number(e.target.value) }))}
                  className="w-full pl-7 pr-3 py-2 border rounded-md text-xs"
                  placeholder="e.g. 500"
                />
              </div>
              {freeShippingThreshold > 0 && (
                <p className="text-[11px] text-blue-500 mt-1">
                  Orders below ₹{freeShippingThreshold} will have shipping
                  charges applied.
                </p>
              )}
            </div>
          )}
        </div>
      </div>}

      {/* ================= SMS / WHATSAPP ================= */}
      <div className="pt-6 border-t">
        <h3 className="text-sm font-medium">SMS / WhatsApp</h3>
        <p className="text-xs text-gray-500 mt-1">
          Configure WhatsApp acknowledgement
        </p>

        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-700">
              Enable WhatsApp Acknowledgement
            </p>
            <Toggle
              enabled={config.isWhatsappBusinessApiEnabled}
              onChange={(enabled: boolean) =>
                handleToggleInput(enabled, "isWhatsappBusinessApiEnabled")
              }
            />
          </div>

          {config.isWhatsappBusinessApiEnabled && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    WhatsApp Business API Key
                  </label>
                  <input
                    type="text"
                    value={config.whatsappBusinessApiKey}
                    onChange={(e) =>
                      handleInputChange(e, "whatsappBusinessApiKey")
                    }
                    className="w-full px-3 py-2 border rounded-md text-xs"
                    placeholder="Enter WhatsApp API Key"
                  />
                </div>
                <div className="flex items-center justify-center bg-gray-300 border rounded-md px-3 py-2 w-full sm:w-auto">
                  <img
                    src="/logo/whatsapp.svg"
                    alt="WhatsApp"
                    className="w-16 sm:w-28 object-contain"
                  />
                </div>
              </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    WhatsApp API Token
                  </label>
                  <div className="relative">
                    <input
                      type={showWhatsappApiToken ? "text" : "password"}
                      value={config.whatsappApiToken || ""}
                      onChange={(e) => handleInputChange(e, "whatsappApiToken")}
                      className="w-full pl-3 pr-12 py-2 border rounded-md text-xs"
                      placeholder="Enter WhatsApp API Token"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowWhatsappApiToken((prev) => !prev)}
                    >
                      {showWhatsappApiToken ? (
                        <EyeOff size={16} strokeWidth={2.5} />
                      ) : (
                        <Eye size={16} strokeWidth={2.5} />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    WhatsApp Phone Number ID
                  </label>
                  <input
                    type="text"
                    value={config.whatsappPhoneNumberId || ""}
                    onChange={(e) =>
                      handleInputChange(e, "whatsappPhoneNumberId")
                    }
                    className="w-full px-3 py-2 border rounded-md text-xs"
                    placeholder="Enter Phone Number ID"
                  />
                </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  WhatsApp API URL
                </label>
                <input
                  type="text"
                  value={config.whatsappApiUrl || ""}
                  onChange={(e) => handleInputChange(e, "whatsappApiUrl")}
                  className="w-full px-3 py-2 border rounded-md text-xs"
                  placeholder="https://graph.facebook.com/v17.0"
                />
              </div>
            </div>
          )}
        </div>
        {/* FAST2SMS */}
        <div className="pt-4 border-t">
          <h3 className="text-sm font-medium">Fast2SMS</h3>

          <div className="mt-3 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-500 mt-1">
                  Fast2SMS API Key
                </label>
                <input
                  type="text"
                  value={config.fast2SmsApiKey || ""}
                  onChange={(e) => handleInputChange(e, "fast2SmsApiKey")}
                  className="w-full px-3 py-2 border rounded-md text-xs"
                  placeholder="Enter Fast2SMS API Key"
                />
              </div>

              <div className="flex items-center justify-center bg-gray-300 border rounded-md px-3 py-2">
                <img
                  src="/logo/fast2sms.png"
                  alt="Fast2SMS"
                  className="w-16 sm:w-24 object-contain"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                Fast2SMS API URL
                {/* <span className="text-[10px] font-normal text-gray-400 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">Read only</span> */}
              </label>
              <input
                type="text"
                value={config.fast2SmsApiUrl || ""}
                readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-xs bg-gray-100 text-gray-400 cursor-not-allowed select-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                OTP Verification Message ID
              </label>
              <input
                type="number"
                onWheel={(e) => e.currentTarget.blur()}
                value={config.f2sOtpVerificationMessageId ?? ""}
                onChange={(e) => handleNumberInputChange(e, "f2sOtpVerificationMessageId")}
                className="w-full px-3 py-2 border rounded-md text-xs"
                placeholder="Enter OTP Verification Message ID"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                OTP Verification Sender ID
              </label>
              <input
                type="text"
                value={config.f2sOtpVerificationSenderId || ""}
                onChange={(e) => handleInputChange(e, "f2sOtpVerificationSenderId")}
                className="w-full px-3 py-2 border rounded-md text-xs"
                placeholder="Enter OTP Verification Sender ID"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Order Success Message ID
              </label>
              <input
                type="number"
                onWheel={(e) => e.currentTarget.blur()}
                value={config.f2sOrderSuccessMessageId ?? ""}
                onChange={(e) => handleNumberInputChange(e, "f2sOrderSuccessMessageId")}
                className="w-full px-3 py-2 border rounded-md text-xs"
                placeholder="Enter Order Success Message ID"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Order Success Sender ID
              </label>
              <input
                type="text"
                value={config.f2sOrderSuccessSenderId || ""}
                onChange={(e) => handleInputChange(e, "f2sOrderSuccessSenderId")}
                className="w-full px-3 py-2 border rounded-md text-xs"
                placeholder="Enter Order Success Sender ID"
              />
            </div>
          </div>
        </div>

        {/* TWILIO */}
        <div className="pt-4 border-t">
          <h3 className="text-sm font-medium mb-2">Twilio</h3>

          <div className="space-y-3">
            {/* Account SID */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Twilio Account SID
              </label>
              <input
                type="text"
                value={config.twilioAccountSid || ""}
                onChange={(e) => handleInputChange(e, "twilioAccountSid")}
                className="w-full px-3 py-2 border rounded-md text-xs"
                placeholder="Enter Twilio Account SID"
              />
            </div>

            {/* Auth Token */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Twilio Auth Token
              </label>
              <div className="relative">
                <input
                  type={showTwilioAuthToken ? "text" : "password"}
                  value={config.twilioAuthToken || ""}
                  onChange={(e) => handleInputChange(e, "twilioAuthToken")}
                  className="w-full pl-3 pr-12 py-2 border rounded-md text-xs"
                  placeholder="Enter Twilio Auth Token"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowTwilioAuthToken((prev) => !prev)}
                >
                  {showTwilioAuthToken ? (
                    <EyeOff size={16} strokeWidth={2.5} />
                  ) : (
                    <Eye size={16} strokeWidth={2.5} />
                  )}
                </button>
              </div>
            </div>

            {/* Outgoing Number */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Twilio Outgoing SMS Number
              </label>
              <input
                type="text"
                value={config.twilioOutgoingSmsNumber || ""}
                onChange={(e) =>
                  handleInputChange(e, "twilioOutgoingSmsNumber")
                }
                className="w-full px-3 py-2 border rounded-md text-xs"
                placeholder="Enter Twilio Outgoing SMS Number"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentExternalAPISettings;
